import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { CONTENT } from "@/content";
import { VerseCard } from "@/components/VerseCard";

const H = CONTENT.heart;
const EASE = [0.2, 0.7, 0.3, 1];

const Reveal = ({ children, delay = 0, className = "", ...rest }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.6, delay, ease: EASE }}
    {...rest}
  >
    {children}
  </motion.div>
);

const Rail = ({ target, step }) => {
  const { scrollYProgress } = useScroll({ target, offset: ["start center", "end center"] });
  return (
    <div className="tl-rail" aria-hidden="true">
      <span className="tl-line" />
      <motion.span className="tl-fill" style={{ scaleY: scrollYProgress }} />
      <motion.span
        className="tl-dot"
        initial={{ backgroundColor: "#fdfaf2", scale: 0.8 }}
        whileInView={{ backgroundColor: "#ed6d2a", scale: 1 }}
        viewport={{ once: true, amount: 1, margin: "0px 0px -45% 0px" }}
        transition={{ duration: 0.4, ease: EASE }}
        data-testid={`timeline-dot-${step}`}
      >
        {step}
      </motion.span>
    </div>
  );
};

const Art = ({ image, flip, step }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const tilt = flip ? 1.5 : -1.5;
  return (
    <motion.div
      ref={ref}
      className="panel-art"
      style={{ y }}
      initial={{ opacity: 0, rotate: tilt * 3, scale: 0.9 }}
      whileInView={{ opacity: 1, rotate: tilt, scale: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.7, ease: EASE }}
    >
      <img src={image.src} alt={image.alt} loading="lazy" data-testid={`story-image-${step}`} />
    </motion.div>
  );
};

const Panel = ({ p, section, first, step }) => {
  const ref = useRef(null);
  const flip = step % 2 === 0;
  return (
    <section ref={ref} className={`story-panel ${flip ? "flip" : ""} ${p.image ? "" : "no-art"}`} data-testid={`story-panel-${step}`}>
      <Rail target={ref} step={step} />
      {p.image && <div className="panel-art-col"><Art image={p.image} flip={flip} step={step} /></div>}
      <div className="panel-text">
        {first && <Reveal><span className="pill">{section.kicker}</span></Reveal>}
        {first && <Reveal delay={0.1}><h2 className="panel-title" data-testid={`story-title-${step}`}>{section.title}</h2></Reveal>}
        {p.paragraphs.map((t, i) => (
          <Reveal key={i} delay={0.15 + i * 0.1}><p className="panel-line">{t}</p></Reveal>
        ))}
        {p.quote && <Reveal delay={0.2}><blockquote className="panel-quote" data-testid="story-prayer-quote">{p.quote}</blockquote></Reveal>}
        {p.highlight && (
          <motion.p
            className="panel-highlight"
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.8 }}
            transition={{ duration: 0.5, delay: 0.2, ease: EASE }}
            data-testid="story-highlight"
          >
            {p.highlight}
          </motion.p>
        )}
      </div>
    </section>
  );
};

const steps = H.sections.reduce((acc, s) => {
  const start = acc.length ? acc[acc.length - 1].at(-1) : 0;
  return [...acc, s.panels.map((_, i) => start + i + 1)];
}, []);

export default function Heart() {
  const { scrollYProgress } = useScroll();
  return (
    <main className="page heart" data-testid="heart-page">
      <motion.div className="scroll-progress" style={{ scaleX: scrollYProgress }} aria-hidden="true" />
      <header className="heart-intro">
        <Reveal><h1 className="heart-title" data-testid="heart-title">{H.title}</h1></Reveal>
        <Reveal delay={0.1}><p className="heart-lead" data-testid="heart-intro">{H.intro}</p></Reveal>
      </header>

      {H.sections.map((s, si) => (
        <div key={s.title} className="story-section" data-testid={`story-section-${si + 1}`}>
          {s.panels.map((p, pi) => <Panel key={pi} p={p} section={s} first={pi === 0} step={steps[si][pi]} />)}
          {s.verse && <div className="heart-verse"><VerseCard {...s.verse} testId={`heart-verse-${si + 1}`} /></div>}
        </div>
      ))}

      <Reveal className="nb-card cta-card" data-testid="heart-closing">
        <p className="cta-line" data-testid="heart-sign-off">{H.closing.signOff}</p>
        <Link to={H.closing.buttonTo} className="nb-btn" data-testid="heart-open-library-button">{H.closing.buttonLabel}</Link>
      </Reveal>
    </main>
  );
}
